import {
  Box,
  Icon,
  Tooltip,
  Text,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { SellersIcon, PackageBox, Globe, PlatformLayout, Email, Phone } from '../../../utils/constants/icons'

import {
  Edge,
  Node,
  Handle,
  Position,
  BaseEdge,
  EdgeProps,
  getBezierPath,
  NodeToolbar,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import type { KrakenGraphResponse, KrakenNode, KrakenNodeLabel } from '../../../types/kraken.types'

const DEFAULT_NODE_SIZE = 56

// Helper function to shorten titles while preserving readability
const shortenTitle = (title: string, maxLength: number = 6): string => {
  if (!title) return ''
  if (title.length <= maxLength) return title

  if (title.includes('.') && (title.includes('http') || title.includes('www'))) {
    const urlParts = title
      .replace(/https?:\/\//, '')
      .replace(/www\./, '')
      .split('/')
    const domain = urlParts[0].split('.')[0]
    return domain.substring(0, maxLength)
  }

  const firstWord = title.split(' ')[0]
  if (firstWord.length <= maxLength) return firstWord

  return title.substring(0, maxLength) + '...'
}

export type CircleNodeData = {
  label: string
  fullTitle: string
  firstLetter: string
  kind: 'SELLER' | 'ITEM' | 'SAW' | 'LINK' | 'SOCIAL' | 'EMAIL' | 'PHONE'
  url?: string
  original?: KrakenNode
  active?: boolean
}

// Fields to exclude from the "extra properties" section
const TOOLTIP_SKIP_KEYS = new Set([
  'id', 'label', 'title', 'name', 'url', 'domain', 'platform', 'price', 'currency',
])

function TooltipRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <HStack spacing={1} w="100%">
      <Text fontSize="xs" color="gray.500" flexShrink={0}>
        {label}:
      </Text>
      <Text fontSize="xs" noOfLines={1}>
        {value}
      </Text>
    </HStack>
  )
}

function NodeTooltipContent({ data }: { data: CircleNodeData }) {
  const original = data.original

  // Collect extra properties (anything not in TOOLTIP_SKIP_KEYS)
  const extraProps = original
    ? Object.entries(original).filter(
        ([key, val]) => !TOOLTIP_SKIP_KEYS.has(key) && val != null && val !== ''
      )
    : []

  return (
    <VStack align="start" p={3} spacing={1.5} maxW="320px" bg="white">
      <HStack>
        <Badge colorScheme="purple" size="sm">
          {data.kind}
        </Badge>
        <Text fontSize="sm" fontWeight="bold" noOfLines={2}>
          {data.fullTitle}
        </Text>
      </HStack>

      {original?.id && <TooltipRow label="ID" value={original.id} />}

      {/* Seller fields */}
      {data.kind === 'SELLER' && original?.name && <TooltipRow label="Name" value={original.name} />}
      {original?.platform && <TooltipRow label="Platform" value={original.platform} />}

      {/* Item fields */}
      {data.kind === 'ITEM' && original?.title && <TooltipRow label="Title" value={original.title} />}
      {data.kind === 'ITEM' && original?.price != null && (
        <TooltipRow
          label="Price"
          value={`${original.currency || ''} ${Number(original.price).toLocaleString()}`}
        />
      )}

      {/* SAW fields */}
      {(data.kind === 'SAW' || data.kind === 'SOCIAL') && original?.domain && (
        <TooltipRow label="Domain" value={original.domain} />
      )}

      {/* URL */}
      {original?.url && <TooltipRow label="URL" value={original.url} />}

      {/* Extra properties from Neptune */}
      {extraProps.length > 0 && (
        <>
          <Box w="100%" h="1px" bg="gray.100" my={0.5} />
          {extraProps.slice(0, 6).map(([key, val]) => (
            <TooltipRow
              key={key}
              label={key.replace(/_/g, ' ')}
              value={typeof val === 'object' ? JSON.stringify(val) : String(val)}
            />
          ))}
          {extraProps.length > 6 && (
            <Text fontSize="10px" color="gray.400">
              +{extraProps.length - 6} more properties
            </Text>
          )}
        </>
      )}
    </VStack>
  )
}

export function CircleNode({ data, selected }: { data: CircleNodeData; selected?: boolean }) {
  const [isHovered, setIsHovered] = useState(false)
  const glow = data.active || selected
  const palette = KIND_COLORS[data.kind] ?? KIND_COLORS.LINK

  const IconByKind = () => {
    if (data.kind === 'SELLER') return <SellersIcon />
    if (data.kind === 'ITEM') return <PackageBox />
    if (data.kind === 'SAW') return <PlatformLayout />
    if (data.kind === 'SOCIAL') return <Globe />
    if (data.kind == 'EMAIL') return <Email />
    if (data.kind == 'PHONE') return <Phone />
    if (data.kind === 'LINK') return <Globe />
    return <PlatformLayout />
  }

  const getBorderStyle = () => {
    return glow
      ? `2px solid ${palette.border}`
      : `2px solid ${palette.border}66`
  }

  const getBoxShadow = () => {
    if (glow) return `0 8px 22px ${palette.glow}, 0 0 0 6px ${palette.ring}`
    if (isHovered) return `0 6px 18px ${palette.glow}`
    return '0 4px 12px rgba(0,0,0,0.08)'
  }

  const getColor = () => palette.icon
  const getBg = () => glow ? palette.bg : '#fff'

  return (
    <>
      <NodeToolbar
        isVisible={isHovered}
        position={Position.Top}
        offset={8}
      >
        <Box
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          p={0}
          zIndex={9999}
        >
          <NodeTooltipContent data={data} />
        </Box>
      </NodeToolbar>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          pointerEvents: 'all',
          cursor: 'default',
        }}
        className="nopan nodrag"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box position="relative" display="inline-block">
          <div
            style={{
              width: DEFAULT_NODE_SIZE,
              height: DEFAULT_NODE_SIZE,
              borderRadius: '999px',
              background: getBg(),
              border: getBorderStyle(),
              boxShadow: getBoxShadow(),
              display: 'grid',
              placeItems: 'center',
              color: getColor(),
              userSelect: 'none',
              transition: 'box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease, background 150ms ease',
            }}
          >
            <Icon viewBox="0 0 24 24" w="22px" h="22px" fill="none">
              <IconByKind />
            </Icon>
          </div>
          {data.original?.verified && (
            <Box
              position="absolute"
              top="-2px"
              right="-2px"
              w="14px"
              h="14px"
              borderRadius="full"
              bg="green.500"
              border="2px solid white"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Box>
          )}
        </Box>

        <div
          style={{
            marginTop: 8,
            maxWidth: 160,
            textAlign: 'center',
            fontSize: 12,
            lineHeight: 1.2,
            color: '#334155',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {data.label}
        </div>

        <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      </div>
    </>
  )
}

// Per-entity-type node styling
export const KIND_COLORS: Record<string, { border: string; bg: string; icon: string; glow: string; ring: string }> = {
  SELLER: { border: '#7c3aed', bg: '#faf5ff', icon: '#4c1d95', glow: 'rgba(124,58,237,0.30)', ring: 'rgba(124,58,237,0.12)' },
  ITEM:   { border: '#2563eb', bg: '#eff6ff', icon: '#1e3a8a', glow: 'rgba(37,99,235,0.30)',  ring: 'rgba(37,99,235,0.12)'  },
  SAW:    { border: '#0d9488', bg: '#f0fdfa', icon: '#134e4a', glow: 'rgba(13,148,136,0.30)', ring: 'rgba(13,148,136,0.12)' },
  SOCIAL: { border: '#db2777', bg: '#fdf2f8', icon: '#831843', glow: 'rgba(219,39,119,0.30)', ring: 'rgba(219,39,119,0.12)' },
  LINK:   { border: '#64748b', bg: '#f8fafc', icon: '#334155', glow: 'rgba(100,116,139,0.20)', ring: 'rgba(100,116,139,0.08)' },
  EMAIL:  { border: '#4f46e5', bg: '#eef2ff', icon: '#3730a3', glow: 'rgba(79,70,229,0.30)',  ring: 'rgba(79,70,229,0.12)'  },
  PHONE:  { border: '#0891b2', bg: '#ecfeff', icon: '#155e75', glow: 'rgba(8,145,178,0.30)',  ring: 'rgba(8,145,178,0.12)'  },
}

// Edge type color map — blue and purple shades
const EDGE_COLORS: Record<string, string> = {
  similar_phone: '#7c3aed',    // violet-600
  similar_email: '#4f46e5',    // indigo-600
  similar_name: '#a855f7',     // purple-500
  similar_address: '#6366f1',  // indigo-500
  sold_by: '#818cf8',         // indigo-400
  listed_on: '#8b5cf6',       // violet-500
  links_to: '#6d28d9',        // violet-700
  same_registrant: '#4338ca', // indigo-700
  same_ip: '#c084fc',         // purple-400
  same_postcode: '#93c5fd',   // blue-300
}

const getEdgeColor = (label?: string): string => {
  return EDGE_COLORS[label || ''] || '#a78bfa'
}

/**
 * Map a weight (1-10) to a color gradient:
 * 1-3: green (low), 4-5: amber (medium), 6-7: orange (high), 8-10: red (critical)
 */
export const getWeightColor = (weight: number): string => {
  if (weight <= 3) return '#22c55e'  // green-500
  if (weight <= 5) return '#eab308'  // yellow-500
  if (weight <= 7) return '#f97316'  // orange-500
  return '#ef4444'                    // red-500
}

/**
 * Get edge color: use weight-based coloring when weight data is available,
 * fall back to edge-type color for structural edges.
 */
const getEdgeDisplayColor = (
  label: string,
  score?: number,
  weight?: number
): string => {
  const similarityEdges = [
    'similar_phone', 'similar_email', 'similar_name',
    'similar_address', 'similar_platform_seller_id',
    'same_registrant', 'same_ip', 'same_postcode',
  ]

  if (similarityEdges.includes(label)) {
    const w = weight ?? (score != null ? Math.round(score * 10) : 5)
    return getWeightColor(w)
  }

  return EDGE_COLORS[label as keyof typeof EDGE_COLORS] || '#94a3b8'
}

// Short human-readable labels for edge types
const EDGE_SHORT_LABELS: Record<string, string> = {
  similar_phone: 'phone',
  similar_email: 'email',
  similar_name: 'name',
  similar_address: 'address',
  sold_by: 'sold by',
  listed_on: 'listed on',
  links_to: 'links to',
  same_registrant: 'registrant',
  same_ip: 'same IP',
  same_postcode: 'postcode',
}

const getEdgeShortLabel = (label?: string): string => {
  return EDGE_SHORT_LABELS[label || ''] || label || ''
}

// Edge tooltip content component
function EdgeTooltipContent({ edge, score }: { edge: { label: string; score?: number; weight?: number }; score?: number }) {
  const color = getEdgeDisplayColor(edge.label, edge.score, edge.weight)
  const displayScore = typeof edge.score === 'number' ? edge.score : score
  return (
    <VStack align="start" p={3} spacing={2} maxW="280px" bg="white">
      <HStack>
        <Badge size="sm" bg={color} color="white" px={2} py={0.5} borderRadius="md">
          {edge.label}
        </Badge>
        {typeof displayScore === 'number' && (
          <Text fontSize="xs" color="gray.500" fontWeight="600">
            {(displayScore * 100).toFixed(0)}% match
          </Text>
        )}
      </HStack>
    </VStack>
  )
}

// Lighten a hex color by mixing with white
function lightenColor(hex: string, amount: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  const lr = Math.round(r + (255 - r) * amount)
  const lg = Math.round(g + (255 - g) * amount)
  const lb = Math.round(b + (255 - b) * amount)
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`
}

// Custom edge: gradient, no arrows, with label on hover
export function KrakenEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style,
  ...props
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const edgeColor = (style?.stroke || getEdgeDisplayColor(
    data?.edgeData?.label as string ?? '',
    data?.edgeData?.score as number | undefined,
    data?.edgeData?.weight as number | undefined,
  )) as string
  const shortLabel = getEdgeShortLabel(data?.edgeData?.label)
  const score = data?.score as number | undefined
  const sw = style?.strokeWidth ?? 1.5
  const op = style?.opacity ?? 0.5
  const gradientId = `edge-gradient-${id}`

  return (
    <>
      <defs>
        <linearGradient id={gradientId} x1={sourceX} y1={sourceY} x2={targetX} y2={targetY} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={edgeColor} stopOpacity={op as number * 0.4} />
          <stop offset="50%" stopColor={edgeColor} stopOpacity={op as number} />
          <stop offset="100%" stopColor={lightenColor(edgeColor, 0.5)} stopOpacity={op as number * 0.3} />
        </linearGradient>
      </defs>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: `url(#${gradientId})`,
          strokeWidth: sw,
          opacity: 1,
          strokeDasharray: '6 4',
        }}
        {...props}
      />
      {/* Hover zone + label pill — invisible wide hit area, pill shows on hover */}
      <foreignObject
        width={80}
        height={24}
        x={labelX - 40}
        y={labelY - 12}
        className="edgebutton-foreignobject"
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <Tooltip
          label={<EdgeTooltipContent edge={data?.edgeData || { label: '' }} score={score} />}
          bg="white"
          color="black"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          p={0}
          placement="top"
          hasArrow
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '2px 8px',
                fontSize: '10px',
                fontWeight: 700,
                color: edgeColor as string,
                whiteSpace: 'nowrap',
                lineHeight: '18px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
              }}
            >
              {shortLabel}
            </div>
          </div>
        </Tooltip>
      </foreignObject>
    </>
  )
}

export const nodeTypes = { circle: CircleNode }
export const edgeTypes = { kraken: KrakenEdge }

export function radialTreeLayout(
  nodes: Node[],
  edges: Edge[],
  rootId?: string,
  opts?: {
    radiusStep?: number
    centerX?: number
    centerY?: number
  }
): Node[] {
  if (!nodes.length) return nodes

  const centerX = opts?.centerX ?? 0
  const centerY = opts?.centerY ?? 0
  const NODE_SPACING = 120 // space between nodes within a cluster

  // --- Step 1: Find root ---
  const nodeSet = new Set(nodes.map((n) => n.id))

  let root = rootId
  if (!root || !nodeSet.has(root)) {
    const activeNode = nodes.find((n) => (n.data as CircleNodeData)?.active)
    root = activeNode?.id || nodes[0].id
  }

  // --- Step 2: Build adjacency & edge type maps ---
  const adj = new Map<string, Set<string>>()
  for (const n of nodes) adj.set(n.id, new Set())

  // Edges grouped by label type
  const edgesByType = new Map<string, Array<{ source: string; target: string }>>()
  for (const e of edges) {
    if (!e?.source || !e?.target) continue
    adj.get(e.source)?.add(e.target)
    adj.get(e.target)?.add(e.source)
    const label = (e.data as any)?.edgeData?.label || e.label || 'unknown'
    if (!edgesByType.has(label)) edgesByType.set(label, [])
    edgesByType.get(label)!.push({ source: e.source, target: e.target })
  }

  // --- Step 3: Detect clusters using Union-Find per edge type ---
  // For each edge type, find connected components. These are the real clusters.
  type Cluster = { edgeType: string; members: Set<string>; hasRoot: boolean }
  const allClusters: Cluster[] = []

  for (const [edgeType, typeEdges] of edgesByType) {
    // Union-Find
    const parent = new Map<string, string>()
    const find = (x: string): string => {
      if (!parent.has(x)) parent.set(x, x)
      if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!))
      return parent.get(x)!
    }
    const union = (a: string, b: string) => {
      const ra = find(a)
      const rb = find(b)
      if (ra !== rb) parent.set(ra, rb)
    }

    // Only include nodes that exist in our node set
    for (const { source, target } of typeEdges) {
      if (nodeSet.has(source) && nodeSet.has(target)) {
        union(source, target)
      }
    }

    // Collect components
    const components = new Map<string, Set<string>>()
    for (const id of parent.keys()) {
      const r = find(id)
      if (!components.has(r)) components.set(r, new Set())
      components.get(r)!.add(id)
    }

    for (const members of components.values()) {
      if (members.size < 2) continue // skip single-node "clusters"
      allClusters.push({
        edgeType,
        members,
        hasRoot: members.has(root),
      })
    }
  }

  // --- Step 4: Assign each node to its primary cluster ---
  // A node might appear in multiple clusters. Assign it to the largest one.
  // Root stays unassigned (it goes to center).
  const nodeCluster = new Map<string, number>() // node id → cluster index
  // Sort clusters: largest first so big clusters get priority
  allClusters.sort((a, b) => b.members.size - a.members.size)

  for (let ci = 0; ci < allClusters.length; ci++) {
    for (const id of allClusters[ci].members) {
      if (id === root) continue
      if (!nodeCluster.has(id)) {
        nodeCluster.set(id, ci)
      }
    }
  }

  // Collect unclustered nodes (not in any cluster, not root)
  const unclustered: string[] = []
  for (const n of nodes) {
    if (n.id !== root && !nodeCluster.has(n.id)) {
      unclustered.push(n.id)
    }
  }

  // --- Step 5: Build cluster groups (only with assigned members) ---
  const clusterGroups: Array<{ edgeType: string; members: string[] }> = []
  for (let ci = 0; ci < allClusters.length; ci++) {
    const members = [...allClusters[ci].members].filter(
      (id) => id !== root && nodeCluster.get(id) === ci
    )
    if (members.length > 0) {
      clusterGroups.push({ edgeType: allClusters[ci].edgeType, members })
    }
  }

  // If there are unclustered nodes, add them as one group
  if (unclustered.length > 0) {
    clusterGroups.push({ edgeType: 'other', members: unclustered })
  }

  // --- Step 6: Position clusters around the root ---
  const posMap = new Map<string, { x: number; y: number }>()
  posMap.set(root, { x: centerX, y: centerY })

  if (clusterGroups.length === 0) {
    // No clusters — just ring everything around root
    const allOthers = nodes.filter((n) => n.id !== root)
    const step = (2 * Math.PI) / (allOthers.length || 1)
    allOthers.forEach((n, i) => {
      const angle = -Math.PI / 2 + i * step
      posMap.set(n.id, {
        x: centerX + 200 * Math.cos(angle),
        y: centerY + 200 * Math.sin(angle),
      })
    })
  } else {
    // Calculate how much space each cluster needs (its "footprint" radius)
    const clusterFootprint = (count: number): number => {
      if (count <= 1) return NODE_SPACING * 0.5
      if (count <= 6) return NODE_SPACING * 1.2
      // concentric rings: estimate outer ring radius
      let placed = 0
      let ring = 0
      while (placed < count) {
        const r = ring === 0 ? 0 : ring * NODE_SPACING
        const cap = ring === 0 ? 1 : Math.max(1, Math.floor((2 * Math.PI * r) / NODE_SPACING))
        placed += cap
        ring++
      }
      return (ring - 1) * NODE_SPACING + NODE_SPACING * 0.5
    }

    // Compute footprints and total circumference needed
    const footprints = clusterGroups.map((g) => clusterFootprint(g.members.length))
    const CLUSTER_GAP = NODE_SPACING * 2 // big gap between clusters
    const totalCircumference =
      footprints.reduce((s, f) => s + f * 2, 0) + CLUSTER_GAP * clusterGroups.length

    // The orbit radius for cluster centers — must be big enough to fit everything
    const orbitRadius = Math.max(400, totalCircumference / (2 * Math.PI))

    // Place each cluster center around the orbit
    let angle = -Math.PI / 2
    const totalArc = 2 * Math.PI

    for (let gi = 0; gi < clusterGroups.length; gi++) {
      const { members } = clusterGroups[gi]
      const footprint = footprints[gi]

      // Angular space this cluster takes up
      const arcLen = footprint * 2 + CLUSTER_GAP
      const clusterArc = (arcLen / totalCircumference) * totalArc
      const clusterCenterAngle = angle + clusterArc / 2

      // Cluster center position
      const cx = centerX + orbitRadius * Math.cos(clusterCenterAngle)
      const cy = centerY + orbitRadius * Math.sin(clusterCenterAngle)

      // Arrange members within the cluster
      if (members.length === 1) {
        posMap.set(members[0], { x: cx, y: cy })
      } else if (members.length <= 6) {
        // Small cluster: single ring
        const r = NODE_SPACING
        members.forEach((id, i) => {
          const a = (2 * Math.PI * i) / members.length - Math.PI / 2
          posMap.set(id, { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) })
        })
      } else {
        // Larger cluster: concentric rings with proper spacing
        let placed = 0
        let ring = 0
        while (placed < members.length) {
          const r = ring === 0 ? 0 : ring * NODE_SPACING
          const capacity =
            ring === 0
              ? 1
              : Math.min(
                  Math.max(1, Math.floor((2 * Math.PI * r) / NODE_SPACING)),
                  members.length - placed
                )

          for (let i = 0; i < capacity && placed < members.length; i++) {
            const a = ring === 0 ? 0 : (2 * Math.PI * i) / capacity - Math.PI / 2
            posMap.set(members[placed], {
              x: cx + r * Math.cos(a),
              y: cy + r * Math.sin(a),
            })
            placed++
          }
          ring++
        }
      }

      angle += clusterArc
    }
  }

  // Apply positions
  return nodes.map((n) => {
    const pos = posMap.get(n.id)
    return {
      ...n,
      position: pos ? { x: pos.x, y: pos.y } : { x: centerX, y: centerY },
    }
  })
}

// Social media domain patterns
const SOCIAL_DOMAINS = [
  'instagram.com', 'facebook.com', 'tiktok.com', 'twitter.com', 'x.com',
  'linkedin.com', 'youtube.com', 'pinterest.com', 'reddit.com', 'tumblr.com',
  'vk.com', 'weibo.com', 'wechat.com', 'telegram.org', 't.me',
]

const isSocialMediaNode = (node: KrakenNode): boolean => {
  const url = (node.url || node.domain || '').toLowerCase()
  return SOCIAL_DOMAINS.some((d) => url.includes(d))
}

// Map Kraken label to internal kind
const mapLabelToKind = (label: KrakenNodeLabel, node?: KrakenNode): CircleNodeData['kind'] => {
  if (label === 'Saw' && node && isSocialMediaNode(node)) return 'SOCIAL'
  switch (label) {
    case 'Item':
      return 'ITEM'
    case 'Seller':
      return 'SELLER'
    case 'Saw':
      return 'SAW'
    case 'Email':
      return 'EMAIL'
    case 'Phone':
      return 'PHONE'
    default:
      return 'LINK'
  }
}

// Transform Kraken response to ReactFlow nodes/edges
export const transformKrakenResponse = (data: KrakenGraphResponse) => {
  const nodeIdSet = new Set<string>()

  const rfNodes: Node<CircleNodeData>[] = data.nodes
    .map((n) => {
      const idStr = String(n.id)
      if (!idStr) return null as unknown as Node<CircleNodeData>

      const fullLabel = n.title || n.name || n.domain || n.url || idStr.slice(0, 6)
      const shortLabel = shortenTitle(fullLabel, 6)

      nodeIdSet.add(idStr)
      return {
        id: idStr,
        type: 'circle',
        position: { x: 0, y: 0 },
        data: {
          label: shortLabel,
          fullTitle: fullLabel,
          firstLetter: '',
          kind: mapLabelToKind(n.label, n),
          original: n,
          url: n.url,
          active: n.id === data.root,
        },
        selectable: true,
        draggable: true,
      } as Node<CircleNodeData>
    })
    .filter(Boolean)

  const rfEdges: Edge[] = data.edges
    .map((e) => {
      const src = String(e.from_id)
      const dst = String(e.to_id)
      if (!src || !dst) return null
      if (!nodeIdSet.has(src) || !nodeIdSet.has(dst)) return null
      const color = getEdgeDisplayColor(e.label, e.score, e.weight)
      // Normalise to 0-1: prefer weight (1-10 scale), fall back to score (0-1), default 0.5
      const rawWeight = typeof e.weight === 'number' ? e.weight / 10 : typeof e.score === 'number' ? e.score : 0.5
      const score = Math.max(0, Math.min(1, rawWeight))
      // Map score to stroke width: 1px (low) to 4px (critical)
      const strokeWidth = 1 + score * 3
      // Map score to opacity: 0.30 (low) to 0.80 (high)
      const opacity = 0.30 + score * 0.50
      return {
        id: `${src}->${dst}-${e.label}`,
        source: src,
        target: dst,
        type: 'kraken',
        animated: false,
        style: { stroke: color, strokeWidth, opacity },
        data: { edgeData: e, score },
      } as Edge
    })
    .filter(Boolean) as Edge[]

  return { nodes: rfNodes, edges: rfEdges }
}
